/**
 * Sprint C3a — Team Management Page (admin-only, /settings/team).
 *
 * Listet alle User (active/invited/deactivated) mit Actions:
 * - Rolle ändern (Admin ↔ Member, mit Last-Admin-Guard UI + server)
 * - Deaktivieren (Self-Deactivate-Guard + Last-Admin-Guard server-mirror)
 * - Reaktivieren (für deactivated rows)
 * - Invite erneut senden (für invited rows, lastVisitedAt IS NULL)
 *
 * Plus: Invite-Modal-Trigger (handelt 409-deactivated mit Recovery-CTA).
 *
 * Patterns:
 * - Tri-state fetch (react.md "null ≠ loading ≠ error")
 * - Single-flight per row-id (admin-ui.md "Optimistic-UI single-flight" via
 *   useRef<Set<string>>)
 * - CSS-Swap Desktop-table / Mobile-card (tailwind.md)
 * - Authoritative-reload-on-error (admin-ui.md)
 *
 * Route-Guard: App.tsx prüft authedUser.isAdmin vor mount; nicht-admin
 * landen via <ForbiddenRedirect /> auf /.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AdminUserRow,
  Role,
  UserStatus,
} from '../../shared/admin-user-types';
import type { AuthedUser } from '../lib/auth-types';
import { api, ApiError } from '../lib/api';
import { useToast } from '../lib/toast-context';
import { InviteUserModal } from '../components/InviteUserModal';
import { Button } from '../components/ui/Button';
import { Pill, type PillTone } from '../components/ui/Pill';
import { EmptyState } from '../components/ui/EmptyState';
import { Card } from '../components/ui/Card';
import { I } from '../lib/icons';
import { useConfirm } from '../components/ui/useConfirm';
import { PAGE_H1_CLASS } from '../components/ui/page-heading';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; users: AdminUserRow[] };

const STATUS_LABEL: Record<UserStatus, string> = {
  active: 'Aktiv',
  invited: 'Eingeladen',
  deactivated: 'Deaktiviert',
};

const STATUS_TONE: Record<UserStatus, PillTone> = {
  active: 'green',
  invited: 'warn',
  deactivated: 'neutral',
};

interface TeamPageProps {
  authedUser: AuthedUser;
}

export function TeamPage({ authedUser }: TeamPageProps) {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const { confirm, confirmDialog } = useConfirm();

  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [refetchKey, setRefetchKey] = useState(0);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  // Client-Filter: Suche (Name/E-Mail) + Status.
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserStatus | 'all'>('all');
  // Single-flight per row-id (admin-ui.md "Optimistic-UI single-flight" +
  // react-hooks.md "useRef-Mutex").
  //
  // ZWEI Layer (Codex PR-R1 [P2]):
  // - `pendingRef`: synchroner ref-backed Lock — atomare check-and-set
  //   ohne React-state-batching-Lag. Rapid-double-click auf derselben
  //   row sieht den 2. Klick als blocked (1. Klick hat schon ge-add-et).
  //   `useState` ALLEIN würde nicht reichen weil pendingIds zum Zeitpunkt
  //   des 2. Klicks noch das alte Set hat (state-update async).
  // - `pendingIds`: triggert React-re-render (button-disabled state).
  //   Wird in derselben tick wie pendingRef mutiert.
  const pendingRef = useRef<Set<string>>(new Set());
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  // ── Fetch ────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    setState({ status: 'loading' });

    (async () => {
      try {
        const res = await api<{ users: AdminUserRow[] }>('/api/admin/users', {
          signal: ac.signal,
        });
        if (cancelled) return;
        setState({ status: 'ok', users: res.users });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Team konnte nicht geladen werden';
        setState({ status: 'error', message });
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [refetchKey]);

  const refetch = useCallback(() => setRefetchKey((k) => k + 1), []);

  // ── Last-Admin-Guard (UI-hint, server is authoritative via DK-17) ──
  const activeAdminCount =
    state.status === 'ok'
      ? state.users.filter((u) => u.role === 'Admin' && u.deletedAt === null)
          .length
      : 0;

  const isLastAdmin = useCallback(
    (row: AdminUserRow): boolean =>
      row.role === 'Admin' && row.deletedAt === null && activeAdminCount === 1,
    [activeAdminCount],
  );

  // ── Mutations ────────────────────────────────────────────────────
  const withSingleFlight = useCallback(
    async <T,>(rowId: string, fn: () => Promise<T>): Promise<T | undefined> => {
      // Atomare check-and-set via ref (Codex PR-R1 [P2] fix).
      // Set.has() + Set.add() laufen synchron in derselben event-loop-tick →
      // rapid-double-click sieht den 2. Klick als bereits-im-Set.
      if (pendingRef.current.has(rowId)) return undefined;
      pendingRef.current.add(rowId);
      // Mirror in React-state für button-disabled re-render.
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.add(rowId);
        return next;
      });
      try {
        return await fn();
      } finally {
        pendingRef.current.delete(rowId);
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(rowId);
          return next;
        });
      }
    },
    [],
  );

  const handleRoleChange = useCallback(
    async (row: AdminUserRow, newRole: Role) => {
      if (newRole === row.role) return;
      if (isLastAdmin(row) && newRole === 'Member') {
        toastRef.current.error('Letzter aktiver Admin kann nicht demoted werden');
        return;
      }
      const ok = await confirm({
        title: 'Rolle ändern?',
        message: `Rolle von ${row.displayName} ändern: ${row.role} → ${newRole}?`,
        confirmLabel: 'Ändern',
      });
      if (!ok) return;
      await withSingleFlight(row.id, async () => {
        try {
          await api(`/api/admin/users/${row.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ role: newRole }),
          });
          toastRef.current.success(`Rolle aktualisiert: ${row.displayName}`);
          refetch();
        } catch (err) {
          const message =
            err instanceof ApiError ? err.message : 'Rolle-Update fehlgeschlagen';
          toastRef.current.error(message);
          refetch();
        }
      });
    },
    [confirm, isLastAdmin, refetch, withSingleFlight],
  );

  const handleDeactivate = useCallback(
    async (row: AdminUserRow) => {
      if (row.id === authedUser.id) return; // self-guard (button is hidden)
      if (isLastAdmin(row)) {
        toastRef.current.error('Letzter aktiver Admin kann nicht deaktiviert werden');
        return;
      }
      const ok = await confirm({
        title: 'User deaktivieren?',
        message: `${row.displayName} deaktivieren?`,
        variant: 'danger',
        confirmLabel: 'Deaktivieren',
      });
      if (!ok) return;
      await withSingleFlight(row.id, async () => {
        try {
          await api(`/api/admin/users/${row.id}`, { method: 'DELETE' });
          toastRef.current.success(`${row.displayName} deaktiviert`);
          refetch();
        } catch (err) {
          const message =
            err instanceof ApiError ? err.message : 'Deaktivierung fehlgeschlagen';
          toastRef.current.error(message);
          refetch();
        }
      });
    },
    [authedUser.id, confirm, isLastAdmin, refetch, withSingleFlight],
  );

  const handleReactivate = useCallback(
    async (row: AdminUserRow) => {
      const ok = await confirm({
        title: 'User reaktivieren?',
        message: `${row.displayName} reaktivieren?`,
        confirmLabel: 'Reaktivieren',
      });
      if (!ok) return;
      await withSingleFlight(row.id, async () => {
        try {
          await api(`/api/admin/users/${row.id}/restore`, { method: 'POST' });
          toastRef.current.success(`${row.displayName} reaktiviert`);
          refetch();
        } catch (err) {
          const message =
            err instanceof ApiError ? err.message : 'Reaktivierung fehlgeschlagen';
          toastRef.current.error(message);
          refetch();
        }
      });
    },
    [confirm, refetch, withSingleFlight],
  );

  const handleResendInvite = useCallback(
    async (row: AdminUserRow) => {
      await withSingleFlight(row.id, async () => {
        try {
          await api(`/api/admin/users/${row.id}/resend-invite`, {
            method: 'POST',
          });
          toastRef.current.success(`Einladung erneut gesendet an ${row.email}`);
        } catch (err) {
          const message =
            err instanceof ApiError ? err.message : 'Erneutes Senden fehlgeschlagen';
          toastRef.current.error(message);
        }
      });
    },
    [withSingleFlight],
  );

  // ── Render ───────────────────────────────────────────────────────
  // normalize('NFC') (2026-06-14): displayName kann NFD sein (gepastet) → ohne
  // NFC verfehlt der Filter Umlaut-Tippeingaben (NFC). email ist ASCII.
  const q = query.trim().normalize('NFC').toLowerCase();
  const filteredUsers =
    state.status === 'ok'
      ? state.users.filter(
          (u) =>
            (q === '' ||
              u.email.toLowerCase().includes(q) ||
              u.displayName.normalize('NFC').toLowerCase().includes(q)) &&
            (statusFilter === 'all' || u.status === statusFilter),
        )
      : [];

  const rowProps = (row: AdminUserRow) => ({
    key: row.id,
    row,
    authedUser,
    isPending: pendingIds.has(row.id),
    isLastAdmin: isLastAdmin(row),
    onRoleChange: handleRoleChange,
    onDeactivate: handleDeactivate,
    onReactivate: handleReactivate,
    onResendInvite: handleResendInvite,
  });

  return (
    <div className="px-4 md:px-10 py-6 md:py-10">
      {/* Header-Band: Titel + Suche/Filter + Einladen */}
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <h1 className={PAGE_H1_CLASS}>Team</h1>
        <div className="flex flex-col sm:flex-row gap-2">
          {state.status === 'ok' && state.users.length > 0 && (
            <>
              <div className="relative">
                <I.search
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-green-600 pointer-events-none"
                />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Name / E-Mail…"
                  aria-label="Team durchsuchen"
                  className="w-full sm:w-56 h-9 pl-9 pr-3 bg-white border border-ink-200 rounded-md text-sm placeholder:text-brand-green-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as UserStatus | 'all')}
                aria-label="Nach Status filtern"
                className="h-9 px-3 rounded-md border border-ink-200 bg-white text-sm text-brand-green-800 hover:border-brand-green-700/40 cursor-pointer"
              >
                <option value="all">Alle Status</option>
                <option value="active">Aktiv</option>
                <option value="invited">Eingeladen</option>
                <option value="deactivated">Deaktiviert</option>
              </select>
            </>
          )}
          <Button variant="primary" size="sm" onClick={() => setInviteModalOpen(true)}>
            User einladen
          </Button>
        </div>
      </div>

      {state.status === 'loading' && <TeamSkeleton />}

      {state.status === 'error' && (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          {state.message}
        </div>
      )}

      {state.status === 'ok' && state.users.length === 0 && (
        <EmptyState
          icon={<I.list size={20} />}
          title="Keine User vorhanden"
          sub="Lade Team erneut oder lade einen User ein."
          action={
            <Button variant="primary" onClick={() => setInviteModalOpen(true)}>
              User einladen
            </Button>
          }
        />
      )}

      {state.status === 'ok' && state.users.length > 0 && (
        <Card title="Mitglieder" count={state.users.length} countLabel="Mitglieder" bodyClassName="p-0">
          {filteredUsers.length === 0 ? (
            <p className="px-4 py-6 text-sm text-brand-green-600">
              Kein Mitglied passt zu den Filtern.
            </p>
          ) : (
            <>
              {/* Desktop table-layout (≥md) */}
              <div className="hidden md:block overflow-x-auto px-4 py-1">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-brand-green-600">
                      <th className="py-2 pr-4">Email</th>
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Rolle</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Letzter Login</th>
                      <th className="py-2 text-right">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((row) => (
                      <TeamRow {...rowProps(row)} layout="desktop" />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile divided rows (<md) */}
              <div className="block md:hidden divide-y divide-ink-100">
                {filteredUsers.map((row) => (
                  <TeamRow {...rowProps(row)} layout="mobile" />
                ))}
              </div>
            </>
          )}
        </Card>
      )}

      {inviteModalOpen && (
        <InviteUserModal
          onClose={() => setInviteModalOpen(false)}
          onSuccess={() => {
            setInviteModalOpen(false);
            refetch();
          }}
          existingUsers={state.status === 'ok' ? state.users : []}
          onReactivate={(rowId) => {
            // 409-deactivated recovery: close modal, trigger reactivate
            setInviteModalOpen(false);
            const row = state.status === 'ok' ? state.users.find((u) => u.id === rowId) : null;
            if (row) void handleReactivate(row);
          }}
          onRefetchUsers={async () => {
            // Stale-list recovery (Codex R2 F6 + PR-R2 [P2]). Single
            // fresh fetch — caller does the retry-lookup. We also update
            // TeamPage's local state so the new list is visible after
            // modal-close.
            const res = await api<{ users: AdminUserRow[] }>('/api/admin/users');
            setState({ status: 'ok', users: res.users });
            return res.users;
          }}
        />
      )}

      {confirmDialog}
    </div>
  );
}

// ── Inline Loading-Skeleton (CSS-Swap Desktop-Table + Mobile-Card) ──
function TeamSkeleton() {
  const ROWS = 5;
  return (
    <div role="status" aria-label="Lädt" aria-busy="true">
      {/* Desktop table-skelett */}
      <div className="hidden md:block">
        <div className="border-b border-ink-200 py-2 flex gap-6">
          <div className="h-3 w-24 bg-ink-100 rounded animate-pulse" />
          <div className="h-3 w-24 bg-ink-100 rounded animate-pulse" />
          <div className="h-3 w-20 bg-ink-100 rounded animate-pulse" />
          <div className="h-3 w-20 bg-ink-100 rounded animate-pulse" />
          <div className="h-3 w-24 bg-ink-100 rounded animate-pulse" />
          <div className="h-3 w-20 bg-ink-100 rounded animate-pulse" />
        </div>
        {Array.from({ length: ROWS }).map((_, i) => (
          <div key={i} className="border-b border-ink-100 py-3 flex items-center gap-6">
            <div className="h-4 w-48 bg-ink-200 rounded animate-pulse" />
            <div className="h-4 w-32 bg-ink-200 rounded animate-pulse" />
            <div className="h-7 w-24 bg-ink-100 rounded animate-pulse" />
            <div className="h-4 w-20 bg-ink-100 rounded animate-pulse" />
            <div className="h-4 w-24 bg-ink-100 rounded animate-pulse" />
            <div className="h-4 w-32 bg-ink-100 rounded animate-pulse" />
          </div>
        ))}
      </div>
      {/* Mobile card-skelett */}
      <div className="block md:hidden space-y-3">
        {Array.from({ length: ROWS }).map((_, i) => (
          <div key={i} className="rounded-lg border border-ink-200 bg-white p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 bg-ink-200 rounded animate-pulse" />
                <div className="h-3 w-1/2 bg-ink-100 rounded animate-pulse" />
              </div>
              <div className="h-5 w-20 bg-ink-100 rounded-full animate-pulse" />
            </div>
            <div className="flex gap-3">
              <div className="h-9 w-24 bg-ink-100 rounded animate-pulse" />
              <div className="h-3 w-32 bg-ink-100 rounded animate-pulse self-center" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Row-Component (desktop = <tr>, mobile = <div>) ─────────────────
interface TeamRowProps {
  row: AdminUserRow;
  authedUser: AuthedUser;
  isPending: boolean;
  isLastAdmin: boolean;
  onRoleChange: (row: AdminUserRow, newRole: Role) => void;
  onDeactivate: (row: AdminUserRow) => void;
  onReactivate: (row: AdminUserRow) => void;
  onResendInvite: (row: AdminUserRow) => void;
  layout: 'desktop' | 'mobile';
}

function TeamRow(props: TeamRowProps) {
  const {
    row,
    authedUser,
    isPending,
    isLastAdmin,
    onRoleChange,
    onDeactivate,
    onReactivate,
    onResendInvite,
    layout,
  } = props;

  const isSelf = row.id === authedUser.id;
  const lastLoginLabel = row.lastVisitedAt
    ? new Date(row.lastVisitedAt).toLocaleDateString('de-DE')
    : '—';

  // Role-dropdown disabled if (a) row is last admin (would demote-to-zero)
  // OR (b) pending mutation OR (c) target is deactivated.
  const roleDisabled = isPending || isLastAdmin || row.deletedAt !== null;
  const roleTitle = isLastAdmin
    ? 'Letzter aktiver Admin kann nicht demoted werden'
    : undefined;

  const RoleDropdown = (
    <select
      value={row.role}
      onChange={(e) => onRoleChange(row, e.target.value as Role)}
      disabled={roleDisabled}
      title={roleTitle}
      aria-label={`Rolle für ${row.displayName}`}
      className={`rounded border border-ink-300 text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
        layout === 'mobile' ? 'min-h-11 px-3 py-2' : 'px-2 py-1'
      }`}
    >
      <option value="Admin">Admin</option>
      <option value="Member">Member</option>
    </select>
  );

  // Mobile-Touch-Target ≥44px für Action-Buttons.
  const mobileTouch = layout === 'mobile' ? 'min-h-11' : '';

  const ActionsCluster = (
    // Desktop rechtsbündig → „Deaktivieren" fluchtet über alle Zeilen, egal ob
    // davor ein „Invite/Reaktivieren" steht (Critique P2). Mobile linksbündig.
    <div className={`flex flex-wrap items-center gap-2 ${layout === 'desktop' ? 'justify-end' : ''}`}>
      {row.status === 'invited' && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onResendInvite(row)}
          disabled={isPending}
          className={mobileTouch}
        >
          Invite erneut senden
        </Button>
      )}
      {row.status === 'deactivated' && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onReactivate(row)}
          disabled={isPending}
          className={mobileTouch}
        >
          Reaktivieren
        </Button>
      )}
      {/* Deactivate button: HIDDEN for self-row (Codex DK-25) + for deactivated row.
          Outline-Danger statt solid (Critique P1) — solid-Rot nur im Confirm-Dialog. */}
      {!isSelf && row.status !== 'deactivated' && (
        <Button
          type="button"
          variant="danger-outline"
          size="sm"
          onClick={() => onDeactivate(row)}
          disabled={isPending || isLastAdmin}
          title={
            isLastAdmin
              ? 'Letzter aktiver Admin kann nicht deaktiviert werden'
              : undefined
          }
          className={mobileTouch}
        >
          Deaktivieren
        </Button>
      )}
    </div>
  );

  if (layout === 'desktop') {
    return (
      <tr className="border-b border-ink-100 hover:bg-ink-50">
        <td className="py-3 pr-4 text-brand-green-700">{row.email}</td>
        <td className="py-3 pr-4 text-brand-green-800">{row.displayName}</td>
        <td className="py-3 pr-4">{RoleDropdown}</td>
        <td className="py-3 pr-4">
          <Pill tone={STATUS_TONE[row.status] ?? 'neutral'}>
            {STATUS_LABEL[row.status]}
          </Pill>
        </td>
        <td className="py-3 pr-4 text-brand-green-600">{lastLoginLabel}</td>
        <td className="py-3">{ActionsCluster}</td>
      </tr>
    );
  }

  // mobile (divided row innerhalb der Mitglieder-Card)
  return (
    <div className="px-4 py-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-brand-green-700 truncate">{row.email}</div>
          <div className="text-sm text-brand-green-600">{row.displayName}</div>
        </div>
        <Pill tone={STATUS_TONE[row.status] ?? 'neutral'}>
          {STATUS_LABEL[row.status]}
        </Pill>
      </div>
      <div className="mt-3 flex items-center gap-3">
        {RoleDropdown}
        <span className="text-xs text-brand-green-600">
          {row.lastVisitedAt ? `Login: ${lastLoginLabel}` : 'Nie eingeloggt'}
        </span>
      </div>
      <div className="mt-3">{ActionsCluster}</div>
    </div>
  );
}
